import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  ChartBar, Trophy, Clock, UsersThree, TrendUp,
  ArrowUp, ArrowDown, Minus, Star
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Cell
} from 'recharts';

const COLORS = ['#FF6B35', '#00E5FF', '#22C55E', '#F59E0B', '#A855F7', '#EC4899'];

function ScoreRing({ score, size = 60 }) {
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? '#22C55E' : score >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="4" />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="4"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-1000" />
      <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
        fill="white" fontSize="14" fontWeight="700" className="transform rotate-90" style={{ transformOrigin: 'center' }}>
        {score}
      </text>
    </svg>
  );
}

export default function StaffAnalyticsPage() {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(3);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/staff/analytics/performance?months=${months}`);
        setData(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [api, months]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <div className="text-neutral-400 text-center py-20">Failed to load analytics</div>;

  const { staff_performance, monthly_attendance_trend, monthly_task_trend, salary_efficiency, summary } = data;

  const radarData = staff_performance.map(s => ({
    name: s.name.split(' ')[0],
    attendance: s.attendance_rate,
    tasks: s.task_completion_rate,
    punctuality: s.punctuality,
    score: s.performance_score
  }));

  return (
    <div data-testid="staff-analytics-page" className="space-y-8 relative">
      <div className="glow-orb-orange -top-20 -left-20 z-0" />
      <div className="glow-orb-teal top-60 right-0 z-0" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between">
        <div>
          <p className="text-neutral-500 text-sm font-medium">{t('staff')} {t('performance')}</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">{t('staff_analytics')}</h1>
        </div>
        <select value={months} onChange={e => setMonths(Number(e.target.value))} data-testid="months-selector"
          className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
          <option value={1}>1 Month</option>
          <option value={3}>3 Months</option>
          <option value={6}>6 Months</option>
          <option value={12}>12 Months</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 relative z-10 animate-fade-up" style={{ animationDelay: '100ms' }}>
        <div className="glass-card p-5" data-testid="summary-total-staff">
          <p className="overline mb-1">{t('staff')}</p>
          <p className="text-2xl font-heading font-bold text-white">{summary.total_staff}</p>
          <p className="text-xs text-neutral-500 mt-1">Active members</p>
        </div>
        <div className="glass-card p-5" data-testid="summary-avg-attendance">
          <p className="overline mb-1">Avg Attendance</p>
          <p className="text-2xl font-heading font-bold text-white">{summary.avg_attendance}%</p>
          <div className="w-full bg-white/5 rounded-full h-1.5 mt-2">
            <div className="h-1.5 rounded-full bg-[#22C55E]" style={{ width: `${summary.avg_attendance}%` }} />
          </div>
        </div>
        <div className="glass-card p-5" data-testid="summary-task-completion">
          <p className="overline mb-1">Task Completion</p>
          <p className="text-2xl font-heading font-bold text-white">{summary.avg_task_completion}%</p>
          <div className="w-full bg-white/5 rounded-full h-1.5 mt-2">
            <div className="h-1.5 rounded-full bg-[#00E5FF]" style={{ width: `${summary.avg_task_completion}%` }} />
          </div>
        </div>
        <div className="glass-card p-5" data-testid="summary-top-performer">
          <p className="overline mb-1">Top Performer</p>
          <div className="flex items-center gap-2 mt-1">
            <Trophy size={20} weight="fill" className="text-[#F59E0B]" />
            <p className="text-lg font-heading font-bold text-white truncate">{summary.top_performer}</p>
          </div>
          <p className="text-xs text-neutral-500 mt-1">Avg Score: {summary.avg_score}/100</p>
        </div>
      </div>

      {/* Staff Performance Cards */}
      <div className="space-y-4 relative z-10">
        <h2 className="overline text-neutral-400 flex items-center gap-2"><Star size={16} weight="fill" className="text-[#FF6B35]" /> Individual Performance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {staff_performance.map((s, idx) => (
            <div key={s.staff_id} className="glass-card p-5 animate-fade-up" data-testid={`staff-card-${s.staff_id}`}
              style={{ animationDelay: `${200 + idx * 80}ms` }}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-white text-base">{s.name}</h3>
                  <p className="text-xs text-neutral-500">{s.role}</p>
                </div>
                <ScoreRing score={s.performance_score} />
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-neutral-500 text-[11px] uppercase tracking-wider">Attendance</p>
                  <p className="text-white font-semibold">{s.attendance_rate}%
                    <span className="text-[10px] text-neutral-500 ml-1">({s.present_days}d)</span>
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500 text-[11px] uppercase tracking-wider">Tasks Done</p>
                  <p className="text-white font-semibold">{s.completed_tasks}/{s.total_tasks}
                    <span className="text-[10px] text-neutral-500 ml-1">({s.task_completion_rate}%)</span>
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500 text-[11px] uppercase tracking-wider">Punctuality</p>
                  <p className="text-white font-semibold">{s.punctuality}%</p>
                </div>
                <div>
                  <p className="text-neutral-500 text-[11px] uppercase tracking-wider">Avg Days/Task</p>
                  <p className="text-white font-semibold">{s.avg_completion_days}d</p>
                </div>
              </div>
              {s.overdue_tasks > 0 && (
                <div className="mt-3 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs font-medium">
                  {s.overdue_tasks} overdue task{s.overdue_tasks > 1 ? 's' : ''}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
        {/* Attendance Trend */}
        <div className="glass-card p-6 animate-fade-up" data-testid="attendance-trend-chart" style={{ animationDelay: '300ms' }}>
          <div className="flex items-center gap-2 mb-5">
            <TrendUp size={18} weight="duotone" className="text-[#22C55E]" />
            <span className="overline">Attendance Trend</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={monthly_attendance_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#737373' }} />
              <YAxis tick={{ fontSize: 11, fill: '#737373' }} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }} />
              <Line type="monotone" dataKey="rate" stroke="#22C55E" strokeWidth={2} dot={{ r: 4, fill: '#22C55E' }} name="Attendance %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Task Completion Trend */}
        <div className="glass-card p-6 animate-fade-up" data-testid="task-trend-chart" style={{ animationDelay: '400ms' }}>
          <div className="flex items-center gap-2 mb-5">
            <ChartBar size={18} weight="duotone" className="text-[#00E5FF]" />
            <span className="overline">Task Completion Trend</span>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly_task_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#737373' }} />
              <YAxis tick={{ fontSize: 11, fill: '#737373' }} />
              <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="completed" fill="#00E5FF" radius={[4, 4, 0, 0]} name="Completed" />
              <Bar dataKey="total" fill="rgba(255,255,255,0.1)" radius={[4, 4, 0, 0]} name="Total" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Salary Efficiency Table */}
      <div className="glass-card p-6 relative z-10 animate-fade-up" data-testid="salary-efficiency" style={{ animationDelay: '500ms' }}>
        <div className="flex items-center gap-2 mb-5">
          <UsersThree size={18} weight="duotone" className="text-[#FF6B35]" />
          <span className="overline">Salary Efficiency</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 text-xs uppercase tracking-wider border-b border-white/5">
                <th className="pb-3 pr-4">Staff</th>
                <th className="pb-3 pr-4">Salary</th>
                <th className="pb-3 pr-4">Tasks Done</th>
                <th className="pb-3">Cost/Task</th>
              </tr>
            </thead>
            <tbody>
              {salary_efficiency.map((s, i) => (
                <tr key={i} className="border-b border-white/5 last:border-0">
                  <td className="py-3 pr-4 text-white font-medium">{s.name}</td>
                  <td className="py-3 pr-4 text-neutral-300">Rs. {s.salary?.toLocaleString('en-IN')}</td>
                  <td className="py-3 pr-4 text-neutral-300">{s.completed_tasks}</td>
                  <td className="py-3">
                    <span className={`font-semibold ${s.cost_per_task > 5000 ? 'text-red-400' : s.cost_per_task > 2000 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {s.cost_per_task > 0 ? `Rs. ${s.cost_per_task.toLocaleString('en-IN')}` : '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
